<template>

  <div id="menu-update-importer" class="ai-importer" v-if="user_confirmed">

    <div class="menu-name" @click="redirectToMenu()">{{ menu.name }}</div>

    <h2>Update from a new menu</h2>

    <ErrorNotice v-if="error != ''" :message="error" :action-label="errorAction()" @action="runErrorAction()" />

    <p class="explainer">Upload photos or a PDF of your updated printed menu. We'll make a copy of this menu, compare it with the new one, and list the changes we find: updated names, descriptions and prices, new items, items that are no longer on the menu, and items that have moved. Nothing changes until you approve it, and approved changes are made on the copy, so your current menu stays exactly as it is.</p>

    <!-- step 1: choose files -->
    <div v-if="proposal === null">

      <MenuFilePicker v-model="files" :disabled="comparing" @error="error = $event" />

      <button v-if="files.length > 0 && !comparing" @click="compare()">Compare with current menu</button>

      <div class="status" v-if="comparing">
        <div class="spinner"></div>
        <p>Copying your menu and comparing it with the new one… this can take a few minutes for longer menus.</p>
      </div>
      <p class="navigate-away-note" v-if="comparing">Feel free to navigate away, you can monitor all menu auto-import and translation jobs here: <router-link to="/ai_jobs">Auto-import and translation jobs</router-link></p>

    </div>


    <p class="kept-copy" v-if="kept_copy">
      The copy <strong>{{ kept_copy.name }}</strong> was still created and is in your menus.
      <span class="link" @click="$router.replace(`/${kept_copy.id}`)">Open it</span>
    </p>

    <p class="ai-cost" v-if="isAdmin() && cost && cost.requests > 0">AI cost: {{ formatAiCost(cost) }}</p>

    <!-- step 2: review -->
    <div class="review" v-if="proposal !== null">

      <div class="review-header">
        <p class="review-title">{{ changeCount() }} {{ changeCount() === 1 ? 'change' : 'changes' }} found</p>
        <p class="review-subtitle" v-if="proposal.provisional_menu">
          Approved changes will be made on <strong>{{ proposal.provisional_menu.name }}</strong>, a copy of this menu with all its items, styles, translations and images.
          {{ proposal.unchanged_count }} {{ proposal.unchanged_count === 1 ? 'entry is' : 'entries are' }} unchanged.
          Untick anything you don't want, and edit the new values if needed.
        </p>
        <p class="review-subtitle" v-if="updates.length > 0">Translations into other languages aren't changed automatically, so check them for updated items afterwards.</p>
        <p class="start-over" v-if="proposal.provisional_menu" @click="redirectToProvisionalMenu()">Open the copy without changes</p>
      </div>

      <div v-if="changeCount() === 0">
        <p class="no-changes">Your menu already matches the new one, so no copy was made.</p>
        <p class="start-over" @click="redirectToMenu()">Back to menu</p>
      </div>

      <!-- updated entries -->
      <div class="change-section" v-if="updates.length > 0">

        <p class="section-title">Updated ({{ updates.length }})</p>

        <div class="entry" v-for="update in updates" :key="'update-' + update.food_id"
          :class="{ 'entry-category': update.kind !== 'item', 'entry-excluded': !update.included }">

          <input type="checkbox" v-model="update.included" class="include-checkbox" />

          <div class="entry-fields">

            <div class="change-field" v-if="update.after.name !== undefined">
              <p class="field-label">Name</p>
              <p class="before">{{ update.before.name }}</p>
              <input type="text" v-model="update.after.name" class="name-input" placeholder="Name" />
            </div>

            <p class="unchanged-name" v-else>{{ update.before.name }}</p>

            <div class="change-field" v-if="update.after.description !== undefined">
              <p class="field-label">Description</p>
              <p class="before">{{ update.before.description || 'No description' }}</p>
              <textarea v-model="update.after.description" placeholder="Description" rows="2"></textarea>
            </div>

            <div class="change-field" v-if="update.after.prices !== undefined">
              <p class="field-label">Prices</p>
              <p class="before">{{ formatPrices(update.before.prices) }}</p>
              <div class="prices">
                <div class="price" v-for="(price, price_index) in update.after.prices" :key="price_index">
                  <input type="number" step="any" min="0" v-model="price.amount" placeholder="Price" />
                  <input type="text" v-model="price.label" placeholder="Unit (e.g. Glass)" />
                  <img src="@/assets/delete_image.svg" @click="update.after.prices.splice(price_index, 1)" />
                </div>
                <p class="add-price" v-if="update.after.prices.length < 3" @click="update.after.prices.push({ amount: '', label: '' })">+ Add price</p>
              </div>
            </div>

          </div>

        </div>

      </div>

      <!-- moved entries -->
      <div class="change-section" v-if="moves.length > 0">

        <p class="section-title">Moved ({{ moves.length }})</p>
        <p class="section-subtitle">These sit in a different place on the new menu. Untick any you want to leave where they are.</p>

        <div class="entry" v-for="move in moves" :key="'move-' + move.food_id"
          :class="{ 'entry-category': move.kind !== 'item', 'entry-excluded': !move.included }">

          <input type="checkbox" v-model="move.included" class="include-checkbox" />

          <div class="entry-fields">
            <p class="unchanged-name">{{ move.name }}</p>
            <p class="before">{{ describePlacement(move.from) }}</p>
            <p class="placement-after">{{ describePlacement(move.to) }}</p>
          </div>

        </div>

      </div>

      <!-- new entries -->
      <div class="change-section" v-if="additions.length > 0">

        <p class="section-title">New ({{ additions.length }})</p>

        <div class="entry" v-for="(addition, index) in additions" :key="'addition-' + index"
          :class="{ 'entry-category': addition.kind !== 'item', 'entry-excluded': !addition.included }">

          <input type="checkbox" v-model="addition.included" class="include-checkbox" />

          <div class="entry-fields">

            <p class="placement">{{ describePlacement(addition) }}</p>

            <div class="entry-top-row">

              <select v-model="addition.kind">
                <option value="major_category">Major category</option>
                <option value="category">Category</option>
                <option value="item">Item</option>
              </select>

              <input type="text" v-model="addition.name" class="name-input" placeholder="Name" />

            </div>

            <div v-if="addition.kind === 'item'">

              <textarea v-model="addition.description" placeholder="Description (optional)" rows="2"></textarea>

              <div class="prices">
                <div class="price" v-for="(price, price_index) in addition.prices" :key="price_index">
                  <input type="number" step="any" min="0" v-model="price.amount" placeholder="Price" />
                  <input type="text" v-model="price.label" placeholder="Unit (e.g. Glass)" />
                  <img src="@/assets/delete_image.svg" @click="addition.prices.splice(price_index, 1)" />
                </div>
                <p class="add-price" v-if="addition.prices.length < 3" @click="addition.prices.push({ amount: '', label: '' })">+ Add price</p>
              </div>


            </div>

          </div>

        </div>

      </div>

      <!-- removed entries -->
      <div class="change-section" v-if="removals.length > 0">

        <p class="section-title">Removed ({{ removals.length }})</p>
        <p class="section-subtitle">These aren't on the new menu. Untick any you want to keep.</p>

        <div class="entry" v-for="removal in removals" :key="'removal-' + removal.food_id"
          :class="{ 'entry-category': removal.kind !== 'item', 'entry-excluded': !removal.included }">

          <input type="checkbox" v-model="removal.included" class="include-checkbox" />

          <div class="entry-fields">
            <p class="removed-name">{{ removal.name }}</p>
            <p class="removed-details" v-if="removal.kind === 'item'">{{ formatPrices(removal.prices) }}<span v-if="removal.description"> · {{ removal.description }}</span></p>
          </div>

        </div>

      </div>

    </div>

    <div class="confirm-changes" v-if="proposal !== null && changeCount() > 0 && !applying" @click="applyChanges()" :class="{ 'confirm-disabled': approvedCount() === 0 }">
      <img src="@/assets/checkmark_normal.svg" />
      <p>Apply {{ approvedCount() }} {{ approvedCount() === 1 ? 'change' : 'changes' }} to the copy</p>
    </div>

    <div class="confirm-changes" v-if="applying">
      <p>Applying changes…</p>
    </div>

  </div>

</template>


<script>
import ErrorNotice from './ErrorNotice'
import signedInOnly from './signedInOnly'
import { formatAiCost } from './aiCost'
import { runAiJob, pollAiJob, findOpenJob, rememberJob, forgetJob } from './aiJob'
import MenuFilePicker from './MenuFilePicker'
import { menuFilesFormData } from './menuFiles'

const editablePrices = prices => prices.map(price => ({ amount: price.amount, label: price.label || '' }))

export default {
  name: "MenuUpdateImporter",
  components: { ErrorNotice, MenuFilePicker },
  data() {
    return {
      menu: {},
      files: [],
      proposal: null,
      updates: [],
      additions: [],
      removals: [],
      moves: [],
      comparing: false,
      applying: false,
      error: '',
      error_code: null,
      cost: null,
      kept_copy: null,
      job_id: null // the comparison run being shown (kept in the URL as ?job=)
    }
  },
  mixins: [signedInOnly],
  methods: {
    // a button under the error, for the errors the user can do something about
    errorAction() {
      return { subscription_expired: 'See plans', ai_limit_reached: 'Contact us' }[this.error_code] || ''
    },
    runErrorAction() {
      this.$router.push(this.error_code === 'subscription_expired' ? '/plans' : '/contact')
    },
    onUserConfirmed() {
      this.$http.plain.get('/app_show_menu_v2/' + this.menuId())
        .then(response => { this.menu = response.data.menu })
      this.resumeJob()
    },
    // Picks up this menu's comparison run if there is one: from the URL, or the latest still running or not
    // yet reviewed.
    async resumeJob() {
      const open = this.$route.query.job ? null : await findOpenJob(this, this.menuId(), 'update')
      const jobId = this.$route.query.job || (open && open.job_id)
      if (!jobId) {
        return
      }
      this.job_id = jobId
      rememberJob(this, jobId)
      this.error = ''
      this.comparing = true
      try {
        const response = await pollAiJob(this, jobId)
        if (response.review_state !== 'pending') {
          this.job_id = null
          forgetJob(this)
          return
        }
        this.showProposal(response.data)
      } catch (error) {
        this.setError(error, 'Something went wrong comparing your menu. Please try again.')
      } finally {
        this.comparing = false
      }
    },
    formatAiCost,
    // AI costs are shown to admins only (the server leaves them out for everyone else too)
    isAdmin() {
      return this.$store.getters.getUserType === 'admin'
    },
    menuId() {
      return this.$router.currentRoute.params.menu_id
    },
    async compare() {
      this.error = ''
      this.kept_copy = null
      this.comparing = true
      try {
        const formData = await menuFilesFormData(this.files)
        const response = await runAiJob(this, this.$http.upload.post(`/app_propose_menu_update/${this.menuId()}`, formData), {
          onStarted: jobId => {
            this.job_id = jobId
            rememberJob(this, jobId)
          }
        })
        this.showProposal(response.data)
      } catch (error) {
        this.setError(error, 'Something went wrong comparing your menu. Please try again.')
      } finally {
        this.comparing = false
      }
    },
    // Shows a finished comparison run's proposed changes for review.
    showProposal(proposal) {
      this.cost = proposal.cost
      this.updates = proposal.updates.map(update => ({
        ...update,
        after: {
          ...update.after,
          ...(update.after.prices ? { prices: editablePrices(update.after.prices) } : {})
        },
        included: true
      }))
      this.additions = proposal.additions.map(addition => ({
        ...addition,
        description: addition.description || '',
        prices: editablePrices(addition.prices),
        included: true
      }))
      this.removals = proposal.removals.map(removal => ({ ...removal, included: true }))
      this.moves = proposal.moves.map(move => ({ ...move, included: true }))
      this.proposal = proposal
    },
    changeCount() {
      return this.updates.length + this.additions.length + this.removals.length + this.moves.length
    },
    approved(changes) {
      return changes.filter(change => change.included)
    },
    approvedCount() {
      return this.approved(this.updates).length + this.approved(this.additions).filter(addition => addition.name.trim() !== '').length + this.approved(this.removals).length + this.approved(this.moves).length
    },
    applyChanges() {
      if (this.approvedCount() === 0) {
        return
      }
      const isItem = change => change.kind === 'item'
      const cleanPrices = prices => prices.filter(price => price.amount !== '' && price.amount !== null)
      const changes = {
        order: this.proposal.order,
        moves: this.approved(this.moves).map(move => move.food_id),
        updates: this.approved(this.updates).map(update => ({
          food_id: update.food_id,
          ...update.after,
          ...(update.after.prices ? { prices: cleanPrices(update.after.prices) } : {})
        })),
        removals: this.approved(this.removals).map(removal => removal.food_id),
        additions: this.approved(this.additions).filter(addition => addition.name.trim() !== '').map(addition => ({
          ref: addition.ref,
          kind: addition.kind,
          name: addition.name,
          description: isItem(addition) ? addition.description : '',
          prices: isItem(addition) ? cleanPrices(addition.prices) : []
        }))
      }
      this.error = ''
      this.applying = true
      runAiJob(this, this.$http.secured.post(`/app_apply_menu_update/${this.proposal.provisional_menu.id}`, { ...changes, job_id: this.job_id }))
        .then(() => this.redirectToProvisionalMenu())
        .catch(error => {
          this.applying = false
          this.setError(error, "Couldn't apply the changes. Please try again.")
        })
    },
    describePlacement(placement) {
      const position = placement.after_name ? `After “${placement.after_name}”` : 'At the start of the menu'
      return placement.section && placement.section !== placement.after_name ? `${position} in ${placement.section}` : position
    },
    formatPrices(prices) {
      if (!prices || prices.length === 0) {
        return 'No price'
      }
      return prices.map(price => price.label ? `${price.amount} (${price.label})` : `${price.amount}`).join(' · ')
    },
    redirectToMenu() {
      this.$router.replace(`/${this.menuId()}`)
    },
    redirectToProvisionalMenu() {
      this.$router.replace(`/${this.proposal.provisional_menu.id}`)
    },
    setError(error, text) {
      const data = (error.response && error.response.data) || {}
      this.error = data.error || error.message || text
      this.error_code = data.code || null
      // A failed run can still have used (and been charged for) AI requests.
      if (data.cost) {
        this.cost = data.cost
      }
      // Copies are never deleted, so a failed comparison still leaves one behind.
      if (data.provisional_menu) {
        this.kept_copy = data.provisional_menu
      }
    }
  }
}
</script>


<style scoped src="./importer.css"></style>

<style scoped>

.kept-copy {
  font-size: 13px;
}

.link {
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.no-changes {
  font-weight: 600;
}

.change-section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0px 0px 10px 0px;
}

.section-subtitle {
  font-size: 13px;
  margin: 0px 0px 10px 0px;
}

.change-field {
  margin-bottom: 10px;
}

.field-label, .placement {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: dimgray;
  margin: 0px 0px 5px 0px;
}

.before {
  font-size: 13px;
  color: #BE1E2D;
  text-decoration: line-through;
  margin: 0px 0px 5px 0px;
}

.placement-after {
  font-size: 13px;
  font-weight: 600;
  color: #006838;
  margin: 0px;
}

.change-field .name-input {
  width: 100%;
}

.unchanged-name {
  font-weight: 600;
  margin: 0px 0px 10px 0px;
}

.removed-name {
  font-weight: 600;
  text-decoration: line-through;
  margin: 0px;
}

.removed-details {
  font-size: 13px;
  color: dimgray;
  margin: 5px 0px 0px 0px;
}

</style>
